import {
  type PlaidAccount,
  type PlaidConnection,
  PlaidService,
} from "@openmoney/proto/openmoney/plaid/v1";

import { useMutation, useQuery } from "@connectrpc/connect-query";
import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/_auth/plaid_accounts")({
  component: RouteComponent,
});

function PlaidAccountRow({ account }: { account: PlaidAccount }) {
  return (
    <tr>
      <td className="font-medium text-sm">{account.name}</td>
      <td className="text-[0.8125rem] text-[--ink-soft]">{account.type}</td>
      <td className="text-[0.8125rem] text-[--ink-muted]">{account.subtype}</td>
      <td className="font-mono text-xs text-[--ink-light]">••••{account.mask}</td>
      <td className="money text-[--green] text-sm">
        {account.currentBalance !== undefined
          ? `$${(account.currentBalance / 100).toFixed(2)}`
          : "—"}
      </td>
      <td className="money text-[--green-soft] text-sm">
        {account.availableBalance !== undefined
          ? `$${(account.availableBalance / 100).toFixed(2)}`
          : "—"}
      </td>
    </tr>
  );
}

function ConnectionGroup({
  connection,
  accounts,
}: {
  connection: PlaidConnection;
  accounts: PlaidAccount[];
}) {
  const refresh = useMutation(PlaidService.method.triggerRefresh);

  return (
    <div className="island-shell rounded-md p-5 mb-5">
      <div className="flex items-center justify-between mb-4 pb-3 border-b border-[--line]">
        <div>
          <p className="island-kicker mb-1">Connected Bank</p>
          <h2 className="m-0 text-lg">{connection.institutionName || "Unknown Institution"}</h2>
          <p className="mt-0.5 text-[0.8125rem] text-[--ink-muted]">
            {accounts.length} account{accounts.length !== 1 ? "s" : ""}
          </p>
        </div>
        <button
          type="button"
          onClick={() => refresh.mutate({ connectionId: connection.id })}
          disabled={refresh.isPending}
        >
          {refresh.isPending ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-separate border-spacing-0">
          <thead>
            <tr className="border-b border-[--line]">
              <th className="py-2 px-2.5 text-left font-semibold text-xs uppercase tracking-wide text-[--ink-muted] font-mono">
                Name
              </th>
              <th className="py-2 px-2.5 text-left font-semibold text-xs uppercase tracking-wide text-[--ink-muted] font-mono">
                Type
              </th>
              <th className="py-2 px-2.5 text-left font-semibold text-xs uppercase tracking-wide text-[--ink-muted] font-mono">
                Subtype
              </th>
              <th className="py-2 px-2.5 text-left font-semibold text-xs uppercase tracking-wide text-[--ink-muted] font-mono">
                Account
              </th>
              <th className="py-2 px-2.5 text-left font-semibold text-xs uppercase tracking-wide text-[--ink-muted] font-mono">
                Current
              </th>
              <th className="py-2 px-2.5 text-left font-semibold text-xs uppercase tracking-wide text-[--ink-muted] font-mono">
                Available
              </th>
            </tr>
          </thead>
          <tbody className="[&_tr]:border-b [&_tr]:border-[--line-soft] [&_tr]:transition-colors [&_tr]:duration-150 [&_tr:hover]:bg-[--bg-soft] [&_td]:py-[0.55rem] [&_td]:px-2.5">
            {accounts.map((account) => (
              <PlaidAccountRow key={account.id} account={account} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function RouteComponent() {
  const { data, isLoading, error } = useQuery(PlaidService.method.getPlaidAccounts);

  // Group accounts by connection ID
  const accountsByConnection = new Map<string, PlaidAccount[]>();
  for (const account of data?.accounts ?? []) {
    const existing = accountsByConnection.get(account.connectionId);
    if (existing) {
      existing.push(account);
    } else {
      accountsByConnection.set(account.connectionId, [account]);
    }
  }

  return (
    <main className="page-wrap pb-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="display-title text-[2rem] mb-1">Connected Banks</h1>
          <p className="text-[--ink-soft] text-sm m-0">
            View and manage your connected financial institutions.
          </p>
        </div>
        <Link to="/connect">
          <button type="button" className="primary">
            Add Bank
          </button>
        </Link>
      </div>

      {isLoading ? (
        <div className="island-shell rounded-md p-8 text-center text-[--ink-muted] text-sm">
          Loading accounts…
        </div>
      ) : error ? (
        <div className="island-shell rounded-md p-5 text-[--green] text-sm">
          Error: {error.message}
        </div>
      ) : !data?.connections || data.connections.length === 0 ? (
        <div className="island-shell rounded-md py-12 px-8 text-center">
          <div className="text-[2.5rem] mb-4 opacity-30">🏦</div>
          <h3 className="mb-3 text-lg">No connections yet</h3>
          <p className="text-[--ink-soft] my-0 mx-auto mb-6 max-w-[420px] text-sm">
            Connect your first bank account to start tracking your finances automatically.
          </p>
          <Link to="/connect">
            <button type="button" className="primary">
              Add Bank
            </button>
          </Link>
        </div>
      ) : (
        data.connections.map((connection) => {
          const accounts = accountsByConnection.get(connection.id) ?? [];
          return (
            <ConnectionGroup key={connection.id} connection={connection} accounts={accounts} />
          );
        })
      )}
    </main>
  );
}
